import { useState } from 'react';
import { apiPost, ApiResponse, ApiError } from '../../services/api.service';

interface WithdrawResult {
  success: boolean;
  withdrawn: number[];
  notApplied: number[];
  errors: Array<{ eventId: number; reason: string }>;
}

interface WithdrawFromEventsParams {
  extra_id: number;
  event_ids: number[];
}

interface UseWithdrawFromEventsReturn {
  withdrawFromEvents: (
    params: WithdrawFromEventsParams
  ) => Promise<WithdrawResult | null>;
  loading: boolean;
  error: string | null;
  data: WithdrawResult | null;
}

/**
 * Custom hook to withdraw from multiple events
 * @returns Object containing withdrawFromEvents function, loading state, error state, and response data
 */
export function useWithdrawFromEvents(): UseWithdrawFromEventsReturn {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<WithdrawResult | null>(null);

  const withdrawFromEvents = async (
    params: WithdrawFromEventsParams
  ): Promise<WithdrawResult | null> => {
    try {
      setLoading(true);
      setError(null);

      const response: ApiResponse<WithdrawResult> =
        await apiPost<WithdrawResult>('/events/withdraw', params);

      if (response.success && response.data) {
        setData(response.data);
        // Success notification is handled by API service using backend message
        return response.data;
      } else {
        // Use the backend's error message directly
        const errorMessage = response.message || response.error || 'Fehler beim Zurückziehen der Terminauswahl';
        setError(errorMessage);
        // Error notification is handled by API service
        return null;
      }
    } catch (err) {
      let errorMessage = 'Ein unerwarteter Fehler ist beim Zurückziehen der Terminauswahl aufgetreten';

      if (err instanceof ApiError) {
        // Check if error has data with message property
        if (err.data && err.data.message) {
          errorMessage = err.data.message;
        } else {
          errorMessage = err.message;
        }
      } else if (err instanceof Error) {
        errorMessage = err.message;
      }

      setError(errorMessage);
      // Error notification is handled by API service
      return null;
    } finally {
      setLoading(false);
    }
  };

  return {
    withdrawFromEvents,
    loading,
    error,
    data,
  };
}
